// File: /opt/dev_environment/assets/workspace/app-init/src/components/UserMenu/ProfileModal.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuthService from '../../services/authService';
import './ProfileModal.css';

function ProfileModal({ onClose }) {
  const navigate = useNavigate();
  const { user, refreshProfile, logout } = useAuth();

  // Form state
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', text: string }

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const toastTimerRef = useRef(null);
  const nameInputRef = useRef(null);

  // Cleanup toast timer
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !showDeleteConfirm) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, showDeleteConfirm]);

  // Show toast with auto-dismiss
  const showToast = useCallback((type, text) => {
    setToast({ type, text });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  // ---- Save Profile ----
  const handleSave = useCallback(async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setToast(null);

    try {
      await AuthService.updateProfile({ full_name: fullName.trim() || null });
      await refreshProfile();
      showToast('success', 'Profil wurde aktualisiert');
    } catch (err) {
      showToast('error', err.message || 'Aktualisierung fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  }, [fullName, refreshProfile, showToast]);

  // ---- Delete Account ----
  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
    setDeleteConfirmText('');
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);

    try {
      await AuthService.deactivateAccount();
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      showToast('error', err.message || 'Konto konnte nicht deaktiviert werden');
      setIsDeleting(false);
    }
  }, [logout, navigate, showToast]);

  // ---- Overlay click ----
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget && !showDeleteConfirm) {
      onClose();
    }
  }, [onClose, showDeleteConfirm]);

  // Derived values
  const displayName = user?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName.charAt(0).toUpperCase();
  const hasChanges = (fullName.trim() || '') !== (user?.full_name || '');
  const deleteEnabled = deleteConfirmText.toLowerCase() === 'löschen';

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
      return new Date(isoString).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  return (
    <div
      className="profile-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Profil bearbeiten"
    >
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="profile-modal__header">
          <h2 className="profile-modal__title">
            <span className="profile-modal__title-icon" role="img" aria-hidden="true">👤</span>
            Profil
          </h2>
          <button
            className="profile-modal__close"
            type="button"
            onClick={onClose}
            aria-label="Dialog schließen"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="profile-modal__body">
          {/* Avatar & Email */}
          <div className="profile-modal__avatar-section">
            <div className="profile-modal__avatar">
              {initials}
            </div>
            <div className="profile-modal__email">{user?.email}</div>
            {user?.is_verified !== undefined && (
              <span className={`profile-modal__verified-badge profile-modal__verified-badge--${user.is_verified ? 'yes' : 'no'}`}>
                {user.is_verified ? '✅ Verifiziert' : '⚠️ Nicht verifiziert'}
              </span>
            )}
          </div>

          {/* Toast */}
          {toast && (
            <div className={`profile-modal__toast profile-modal__toast--${toast.type}`} role="alert">
              {toast.type === 'success' ? '✅' : '❌'} {toast.text}
            </div>
          )}

          {/* Edit Form */}
          <form className="profile-modal__form" onSubmit={handleSave}>
            <div className="profile-modal__field">
              <label className="profile-modal__label" htmlFor="profile-email">
                E-Mail
              </label>
              <input
                id="profile-email"
                className="profile-modal__input profile-modal__input--readonly"
                type="email"
                value={user?.email || ''}
                readOnly
                tabIndex={-1}
              />
            </div>

            <div className="profile-modal__field">
              <label className="profile-modal__label" htmlFor="profile-name">
                Name
              </label>
              <input
                ref={nameInputRef}
                id="profile-name"
                className="profile-modal__input"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Dein Name (optional)"
                autoComplete="name"
                maxLength={255}
                disabled={isSaving}
              />
            </div>

            <button
              className="profile-modal__save-btn"
              type="submit"
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? (
                <>
                  <span className="profile-modal__save-spinner" />
                  Wird gespeichert…
                </>
              ) : (
                'Änderungen speichern'
              )}
            </button>
          </form>

          {/* Account Info */}
          <div className="profile-modal__info-section">
            {user?.created_at && (
              <div className="profile-modal__info-row">
                <span>Konto erstellt</span>
                <span className="profile-modal__info-value">{formatDate(user.created_at)}</span>
              </div>
            )}
            {user?.last_login_at && (
              <div className="profile-modal__info-row">
                <span>Letzte Anmeldung</span>
                <span className="profile-modal__info-value">{formatDate(user.last_login_at)}</span>
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="profile-modal__danger-zone">
            {!showDeleteConfirm ? (
              <>
                <div className="profile-modal__danger-title">
                  <span role="img" aria-hidden="true">⚠️</span>
                  Gefahrenzone
                </div>
                <p className="profile-modal__danger-description">
                  Dein Konto wird deaktiviert. Alle Daten bleiben erhalten, aber du kannst dich nicht mehr anmelden.
                </p>
                <button
                  className="profile-modal__delete-btn"
                  type="button"
                  onClick={handleDeleteClick}
                >
                  <span role="img" aria-hidden="true">🗑️</span>
                  Konto deaktivieren
                </button>
              </>
            ) : (
              <div className="profile-modal__delete-confirm">
                <div className="profile-modal__delete-confirm-icon" role="img" aria-hidden="true">
                  ⚠️
                </div>
                <h3 className="profile-modal__delete-confirm-title">
                  Konto wirklich deaktivieren?
                </h3>
                <p className="profile-modal__delete-confirm-message">
                  Gib <strong>„löschen"</strong> ein, um dein Konto zu deaktivieren.
                  Du wirst abgemeldet und kannst dich nicht mehr anmelden.
                </p>
                <input
                  className="profile-modal__delete-confirm-input"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="löschen"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                  disabled={isDeleting}
                  autoFocus
                />
                <div className="profile-modal__delete-confirm-actions">
                  <button
                    className="profile-modal__delete-confirm-btn profile-modal__delete-confirm-btn--cancel"
                    type="button"
                    onClick={handleDeleteCancel}
                    disabled={isDeleting}
                  >
                    Abbrechen
                  </button>
                  <button
                    className="profile-modal__delete-confirm-btn profile-modal__delete-confirm-btn--confirm"
                    type="button"
                    onClick={handleDeleteConfirm}
                    disabled={!deleteEnabled || isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="profile-modal__save-spinner" />
                        Wird deaktiviert…
                      </>
                    ) : (
                      'Konto deaktivieren'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileModal;